import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import IslamFuturisticShell from '../../components/islam/IslamFuturisticShell';
import islamService from '../../services/islamService';
import { formatDate } from '../../utils/helpers';

function renderContent(text) {
  if (!text) return null;
  return text.split(/\n\n+/).map((para, i) => (
    <p key={i} className="mb-4 text-slate-200 leading-relaxed last:mb-0">
      {para.split('\n').map((line, j, arr) => (
        <React.Fragment key={j}>
          {line}
          {j < arr.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </p>
  ));
}

const IslamArticleDetailPage = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await islamService.getContent(slug);
        if (!cancelled) setItem(data);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError('Article not found or unavailable.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (slug) load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <IslamFuturisticShell
        browserTitle="Loading…"
        icon="📰"
        heading="Loading"
        accentOrbClass="bg-cyan-400/30"
      >
        <p className="text-slate-400 text-center">Loading article…</p>
      </IslamFuturisticShell>
    );
  }

  if (error || !item) {
    return (
      <IslamFuturisticShell
        browserTitle="Not found — Islam | PakPoints"
        icon="📰"
        heading="Article not found"
        accentOrbClass="bg-cyan-400/30"
      >
        <p className="text-slate-400 mb-4">{error}</p>
        <Link to="/islam/articles" className="text-cyan-300 hover:text-cyan-200">
          ← Back to articles
        </Link>
      </IslamFuturisticShell>
    );
  }

  return (
    <IslamFuturisticShell
      browserTitle={`${item.title} — Islam | PakPoints`}
      metaDescription={(item.content || '').replace(/\s+/g, ' ').slice(0, 160)}
      icon="📰"
      heading={item.title}
      tagline={
        [item.category_display, item.published_date && formatDate(item.published_date), item.author_name]
          .filter(Boolean)
          .join(' · ') || undefined
      }
      accentOrbClass="bg-cyan-400/30"
    >
      <article
        className={`prose prose-invert max-w-none ${item.language === 'ur' ? 'text-right' : 'text-left'}`}
        dir={item.language === 'ur' ? 'rtl' : 'ltr'}
        lang={item.language === 'ur' ? 'ur' : 'en'}
      >
        {renderContent(item.content)}
      </article>
      <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-4 items-center">
        <Link to="/islam/articles" className="text-cyan-300 hover:text-cyan-200 text-sm font-medium">
          ← All Islamic articles
        </Link>
        {item.alternate_slug ? (
          <Link
            to={`/islam/articles/${item.alternate_slug}`}
            className="text-sm font-medium text-violet-300 hover:text-violet-200"
          >
            {item.language === 'ur' ? 'Read in English' : 'اردو میں پڑھیں'}
          </Link>
        ) : null}
      </div>
    </IslamFuturisticShell>
  );
};

export default IslamArticleDetailPage;
